<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TeletelBrandingAPI from 'dashboard/api/teletel/branding';
import NextButton from 'dashboard/components-next/button/Button.vue';

// Three modes, mirroring the server exactly: default keeps the stock footer,
// hidden removes it, custom replaces it with the account's own text and link.
// The server re-validates everything; the checks here only save a round trip.
const { t } = useI18n();

const MODES = ['default', 'hidden', 'custom'];
const TEXT_LIMIT = 80;

const loading = ref(true);
const saving = ref(false);
const mode = ref('default');
const text = ref('');
const url = ref('');

const isCustom = computed(() => mode.value === 'custom');
const urlInvalid = computed(
  () => Boolean(url.value.trim()) && !/^https?:\/\//.test(url.value.trim())
);
const textMissing = computed(() => isCustom.value && !text.value.trim());
const textTooLong = computed(() => text.value.trim().length > TEXT_LIMIT);
const canSave = computed(
  () =>
    !loading.value &&
    !saving.value &&
    (!isCustom.value ||
      (!textMissing.value && !textTooLong.value && !urlInvalid.value))
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await TeletelBrandingAPI.show();
    mode.value = data.mode || 'default';
    text.value = data.text || '';
    url.value = data.url || '';
  } catch (error) {
    useAlert(t('TELETEL_BRANDING.LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const save = async () => {
  saving.value = true;
  try {
    const { data } = await TeletelBrandingAPI.update({
      mode: mode.value,
      text: text.value.trim(),
      url: url.value.trim(),
    });
    // The server validates and is the source of truth for what was stored.
    mode.value = data.mode;
    text.value = data.text || '';
    url.value = data.url || '';
    useAlert(t('TELETEL_BRANDING.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.error || t('TELETEL_BRANDING.SAVE_ERROR'));
  } finally {
    saving.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="flex flex-col w-full gap-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-lg font-medium text-n-slate-12">
          {{ t('TELETEL_BRANDING.TITLE') }}
        </h1>
        <p class="mt-1 mb-0 text-sm text-n-slate-11 max-w-prose">
          {{ t('TELETEL_BRANDING.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        blue
        sm
        :is-loading="saving"
        :disabled="!canSave"
        @click="save"
      >
        {{ t('TELETEL_BRANDING.SAVE') }}
      </NextButton>
    </div>

    <div v-if="loading" class="py-8 text-sm text-center text-n-slate-11">
      {{ t('TELETEL_BRANDING.LOADING') }}
    </div>

    <template v-else>
      <ul class="flex flex-col gap-2 p-0 m-0 list-none">
        <li
          v-for="option in MODES"
          :key="option"
          class="flex items-start gap-3 px-3 py-2.5 border rounded-lg cursor-pointer border-n-weak hover:border-n-slate-7"
          :class="mode === option ? 'bg-n-brand/5 border-n-brand' : ''"
          @click="mode = option"
        >
          <input
            type="radio"
            class="mt-1 m-0 shrink-0"
            :checked="mode === option"
            :aria-label="t(`TELETEL_BRANDING.MODE_${option.toUpperCase()}`)"
            @click.stop="mode = option"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-sm text-n-slate-12">
              {{ t(`TELETEL_BRANDING.MODE_${option.toUpperCase()}`) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ t(`TELETEL_BRANDING.MODE_${option.toUpperCase()}_HINT`) }}
            </span>
          </div>
        </li>
      </ul>

      <div
        v-if="isCustom"
        class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak"
      >
        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('TELETEL_BRANDING.TEXT_LABEL') }}
          <input
            v-model="text"
            type="text"
            :maxlength="TEXT_LIMIT"
            class="mb-0 text-sm"
          />
        </label>
        <p v-if="textMissing" class="mb-0 text-sm text-n-ruby-11">
          {{ t('TELETEL_BRANDING.TEXT_REQUIRED') }}
        </p>

        <label class="flex flex-col gap-1 text-sm text-n-slate-11">
          {{ t('TELETEL_BRANDING.URL_LABEL') }}
          <input
            v-model="url"
            type="url"
            dir="ltr"
            :placeholder="t('TELETEL_BRANDING.URL_PLACEHOLDER')"
            class="mb-0 text-sm"
          />
        </label>
        <p v-if="urlInvalid" class="mb-0 text-sm text-n-ruby-11">
          {{ t('TELETEL_BRANDING.URL_INVALID') }}
        </p>
      </div>

      <p class="mb-0 text-xs text-n-slate-10 max-w-prose">
        {{ t('TELETEL_BRANDING.COVERAGE_HINT') }}
      </p>
    </template>
  </div>
</template>
